(function (global) {
  'use strict';
  if (global.GiftsboxlySecurity) return;
  const purifier = global.DOMPurify(global);
  const commercePath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:cart(?:\/|\.|$)|products(?:\/|$)|collections(?:\/|$)|search(?:\/|\.|$)|recommendations\/products(?:\.|$)|variants\/|discount\/)/i;
  function resolveCommerceURL(input) {
    const url = new URL(String(input), global.location.origin);
    if (url.origin !== global.location.origin || !/^https?:$/.test(url.protocol) || url.username || url.password || !commercePath.test(url.pathname)) {
      throw new TypeError('This request is outside the storefront commerce routes.');
    }
    return url;
  }
  async function request(input, options) {
    const url = resolveCommerceURL(input);
    const discount = url.pathname.match(/^(.*\/)discount\/([^/]+)$/);
    if (discount) {
      url.pathname = discount[1] + 'cart/update.js';
      url.search = '';
      options = { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: decodeURIComponent(discount[2]) }) };
    }
    // Keep later Shopify observers and local interceptors in the request path.
    return global.fetch(url.href, Object.assign({}, options || {}, { credentials: 'same-origin', mode: 'same-origin', redirect: 'error' }));
  }
  function formURL(input) {
    const url = new URL(String(input), global.location.origin);
    if (url.origin !== global.location.origin || !/^https?:$/.test(url.protocol) || url.username || url.password) throw new TypeError('Storefront forms and redirects must stay on this store.');
    return url.href;
  }
  function navigate(input) {
    global.location.assign(formURL(input));
  }
  function resourceAllowed(value) {
    if (!value) return true;
    if (/^data:image\/(?:png|jpeg|gif|webp);base64,/i.test(value)) return true;
    if (value.startsWith('blob:' + global.location.origin + '/')) return true;
    try {
      const url = new URL(value, global.location.origin);
      return /^https?:$/.test(url.protocol) && !url.username && !url.password && (url.origin === global.location.origin || url.protocol === 'https:' && url.hostname === 'cdn.shopify.com');
    } catch (_) { return false; }
  }
  function mediaFrameAllowed(value) {
    try {
      const url = new URL(value, global.location.origin);
      if (url.protocol !== 'https:' || url.username || url.password || url.port) return false;
      return (['www.youtube.com', 'www.youtube-nocookie.com'].includes(url.hostname) && /^\/embed\/[\w-]+\/?$/.test(url.pathname)) ||
        (url.hostname === 'player.vimeo.com' && /^\/video\/\d+\/?$/.test(url.pathname));
    } catch (_) { return false; }
  }
  function setResource(target, name, value) {
    const text = value == null ? '' : String(value).trim();
    const values = name === 'srcset' ? text.split(',').map(part => part.trim().split(/\s+/)[0]) : [text];
    const allowed = target.nodeName === 'IFRAME' && name === 'src' ? mediaFrameAllowed(text) : values.every(resourceAllowed);
    if (text && allowed) target.setAttribute(name, text); else target.removeAttribute(name);
    return text;
  }
  purifier.addHook('uponSanitizeElement', node => {
    if (node.nodeName === 'SCRIPT' && (node.hasAttribute('src') || !/^(?:application\/json|application\/ld\+json)$/.test(node.type))) node.remove();
    if (node.nodeName === 'IFRAME' && !mediaFrameAllowed(node.getAttribute('src'))) node.remove();
  });
  purifier.addHook('uponSanitizeAttribute', (node, attribute) => {
    const name = attribute.attrName.toLowerCase();
    const value = attribute.attrValue.trim();
    if (name === 'name' && value === 'id' && node.nodeName === 'INPUT' && node.type === 'hidden') attribute.forceKeepAttr = true;
    if (name === 'form' && ['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'OUTPUT', 'FIELDSET'].includes(node.nodeName) && /^(?:CartDrawer-Form|cart|product-form-[\w-]+)$/.test(value)) attribute.forceKeepAttr = true;
    if (name.startsWith('on') || ['srcdoc', 'ping', 'formaction', 'is'].includes(name)) attribute.keepAttr = false;
    else if (['src', 'poster', 'action', 'href', 'xlink:href', 'background', 'data-src', 'data-poster'].includes(name)) {
      if (!(node.nodeName === 'IFRAME' && name === 'src' ? mediaFrameAllowed(value) : resourceAllowed(value))) attribute.keepAttr = false;
      if (name === 'action') { try { formURL(value); } catch (_) { attribute.keepAttr = false; } }
    } else if (name === 'srcset' || name === 'data-srcset') {
      const safe = value.split(',').map(part => part.trim()).filter(part => resourceAllowed(part.split(/\s+/)[0]));
      if (safe.length) attribute.attrValue = safe.join(', '); else attribute.keepAttr = false;
    } else if (name === 'style' || node.namespaceURI === 'http://www.w3.org/2000/svg' && ['fill', 'stroke', 'filter', 'clip-path', 'mask', 'marker', 'marker-start', 'marker-mid', 'marker-end', 'color-profile', 'cursor'].includes(name)) {
      const css = name === 'style' ? value : value.replace(/url\(\s*(['"]?)#[\w:.-]+\1\s*\)/gi, '');
      if (/(?:url|image-set|image|cross-fade|src)\s*\(|@import|expression\s*\(|behavior\s*:|javascript:|\\|\/\*/i.test(css)) attribute.keepAttr = false;
    }
  });
  function sanitizeHTML(html) {
    if (typeof html !== 'string') throw new TypeError('Expected section HTML.');
    return purifier.sanitize(html, {
      ADD_TAGS: ['script', 'iframe'],
      ADD_ATTR: ['allow', 'allowfullscreen'],
      FORBID_TAGS: ['object', 'embed', 'base', 'link', 'style', 'meta', 'foreignObject', 'annotation-xml'],
      FORBID_ATTR: ['srcdoc', 'ping', 'formaction', 'is'],
      CUSTOM_ELEMENT_HANDLING: { tagNameCheck: /^[a-z][a-z0-9]*-[a-z0-9-]+$/, attributeNameCheck: /^[a-z][a-z0-9_-]*$/, allowCustomizedBuiltInElements: false }
    });
  }
  function replaceHTML(target, html) {
    const allowed = target && target.nodeType === 1 && target.matches('cart-drawer,cart-items,cart-drawer-items,product-info,predictive-search,product-recommendations,#CartDrawer,#CartDrawer-CartItems,#cart-icon-bubble,#main-cart-items,[id^="ProductInfo-"],[id^="quantity-breaks-"]');
    if (!allowed) throw new TypeError('Only a known storefront component can be updated.');
    target.innerHTML = sanitizeHTML(html);
  }
  function writeHTML(target, html, outer) {
    if (!target || target.nodeType !== 1 || target.matches('html,head,body,main,header,footer,#MainContent,#shopify-section-header,#shopify-section-footer')) throw new TypeError('Global page containers cannot be replaced.');
    const safe = sanitizeHTML(html == null ? '' : String(html));
    if (outer) target.outerHTML = safe; else target.innerHTML = safe;
    return safe;
  }
  Object.defineProperty(global, 'GiftsboxlySecurity', { value: Object.freeze({ request, formURL, navigate, sanitizeHTML, replaceHTML, writeHTML, resourceAllowed, setResource }), writable: false, configurable: false });
})(window);
